import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { getNotionStatus, saveNotionConfig, verifyNotion, discoverNotion, provisionNotion, pullNotion, getSchedulerConfig, saveSchedulerConfig, errText } from "../lib/api";

const TARGETS = [
  ["leads_intakes", "Leads & Intakes", "write"],
  ["artists_producers", "Artists & Producers", "write"],
  ["touchpoints", "Touchpoints", "write"],
  ["documents", "Documents", "write"],
  ["service_catalog", "Service Catalog", "read-only"],
  ["product_lines", "Product Lines", "read-only"],
];

export default function NotionSettings() {
  const [status, setStatus] = useState(null);
  const [dbs, setDbs] = useState({});
  const [busy, setBusy] = useState(false);
  const [bulk, setBulk] = useState("");
  const [provisioned, setProvisioned] = useState(null);

  const load = () => getNotionStatus().then((s) => { setStatus(s); setDbs(s.databases || {}); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try { setStatus(await saveNotionConfig(dbs)); toast.success("Database URLs saved and converted to UUIDs."); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try { setStatus(await verifyNotion()); toast.success("Verified against the live Notion schema."); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const detect = async () => {
    setBusy(true);
    try {
      const r = await discoverNotion(bulk.split(/\s+/).filter(Boolean));
      setStatus(r.status);
      setDbs(r.status.databases || {});
      toast.success(`Matched ${r.matched.length} database${r.matched.length === 1 ? "" : "s"} by title.`);
      if (r.unmatched.length) toast.error(`${r.unmatched.length} URL(s) could not be matched.`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const provision = async () => {
    setBusy(true);
    try {
      const r = await provisionNotion();
      setProvisioned(r.provisioned);
      setStatus(r.status);
      toast.success("Notion columns aligned with the form fields.");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const pull = async () => {
    setBusy(true);
    try {
      const r = await pullNotion();
      const pulled = r.results.filter((x) => x.action === "pulled").length;
      const pushed = r.results.filter((x) => x.action === "pushed").length;
      toast.success(`Reconciled ${r.checked} lead(s): ${pulled} pulled from Notion, ${pushed} pushed up.`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };


  return (
    <div className="p-6 md:p-10">
      <h1 className="font-display text-3xl font-black text-zinc-50">Notion Sync</h1>
      <p className="mt-2 max-w-2xl text-sm text-zinc-500">
        Paste each Sabrea OS database URL. They are converted to UUIDs server-side. The integration token lives only in the backend environment and is never sent to the browser.
      </p>

      {status && (
        <div className="mt-8 border border-zinc-900 bg-zinc-900/40 p-6" data-testid="notion-status">
          <div className="flex flex-wrap gap-x-12 gap-y-4">
            <div>
              <p className="font-mono-tech text-[10px] text-zinc-500">Mode</p>
              <p className={`mt-1 font-display text-xl font-black ${status.token_configured ? "text-green-400" : "text-brand-pink"}`} data-testid="notion-mode">{status.mode}</p>
            </div>
            <div>
              <p className="font-mono-tech text-[10px] text-zinc-500">Token</p>
              <p className="mt-1 font-mono-tech text-xs text-zinc-300">{status.token_configured ? "configured" : "not configured"}</p>
            </div>
            <div>
              <p className="font-mono-tech text-[10px] text-zinc-500">API version</p>
              <p className="mt-1 font-mono-tech text-xs text-zinc-300">{status.api_version}</p>
            </div>
            <div>
              <p className="font-mono-tech text-[10px] text-zinc-500">Last verified</p>
              <p className="mt-1 font-mono-tech text-xs text-zinc-300">{status.last_verified?.slice(0, 19).replace("T", " ") || "never"}</p>
            </div>
          </div>
          {!status.token_configured && (
            <p className="mt-5 border-l-2 border-brand-pink pl-4 text-sm leading-relaxed text-zinc-400">
              Running in local-only mode. Everything is stored in this app's database. To activate live sync: create an internal integration at notion.so/profile/integrations, grant it edit access to each database, then set <span className="font-mono-tech text-brand-pink">NOTION_TOKEN</span> in the backend environment and restart the backend.
            </p>
          )}
        </div>
      )}

      <div className="mt-10 border border-zinc-900 bg-zinc-900/30 p-6" data-testid="notion-autodetect">
        <p className="font-mono-tech text-[10px] text-brand-indigo">Auto-detect</p>
        <p className="mt-2 text-sm text-zinc-400">Paste any number of Notion database URLs. Each is resolved and assigned to the right target by its database title.</p>
        <Textarea data-testid="notion-bulk-urls" rows={3} value={bulk} onChange={(e) => setBulk(e.target.value)}
          placeholder={"https://www.notion.so/…\nhttps://www.notion.so/…"}
          className="mt-3 rounded-sm border-zinc-800 bg-zinc-900/70 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-brand-pink" />
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={detect} disabled={busy || !bulk.trim()} data-testid="detect-notion-btn"
            className="h-10 rounded-full bg-brand-indigo px-6 font-semibold text-white hover:bg-brand-indigo-soft transition-colors duration-200">Detect & assign</Button>
          <Button onClick={provision} disabled={busy} data-testid="provision-notion-btn"
            className="h-10 rounded-full border border-zinc-800 bg-transparent px-6 text-zinc-300 hover:border-brand-pink hover:text-zinc-50 transition-colors duration-200">Align columns with form fields</Button>
          <Button onClick={pull} disabled={busy} data-testid="pull-notion-btn"
            className="h-10 rounded-full border border-zinc-800 bg-transparent px-6 text-zinc-300 hover:border-brand-indigo hover:text-zinc-50 transition-colors duration-200">Pull from Notion now</Button>
        </div>
        <p className="mt-4 font-mono-tech text-[10px] text-zinc-500">Two-way reconcile runs automatically every 15 minutes. Most recently edited side wins on Status, Primary Need and Notes.</p>
        {provisioned && (
          <div className="mt-5 space-y-2" data-testid="provision-result">
            {Object.entries(provisioned).map(([k, v]) => (
              <p key={k} className="font-mono-tech text-[10px] text-zinc-400">
                {k}: {v.skipped ? v.skipped : v.error ? `error — ${v.error}` : v.added?.length ? `added ${v.added.join(", ")}` : "already aligned"}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10">
        {TARGETS.map(([key, label, access]) => {
          const res = status?.resolved?.[key];
          return (
            <div key={key} className="border-b border-zinc-900 py-5" data-testid={`notion-target-${key}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-display font-bold text-zinc-50">{label} <span className="ml-2 font-mono-tech text-[9px] text-zinc-500">{access}</span></p>
                {res && (res.ok || res.valid_url) && (
                  <span className={`inline-flex items-center gap-1.5 font-mono-tech text-[10px] ${res.ok === false ? "text-red-400" : res.ok ? "text-green-400" : "text-brand-indigo"}`}>
                    {res.ok === false ? <XCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                    {res.ok === false ? res.error?.slice(0, 60) : res.ok ? `${res.title || "linked"} · ${res.columns?.length} columns` : res.uuid}
                  </span>
                )}
              </div>
              <Input data-testid={`notion-url-${key}`} value={dbs[key] || ""} onChange={(e) => setDbs({ ...dbs, [key]: e.target.value })}
                placeholder="https://www.notion.so/…?v=…"
                className="mt-3 h-11 rounded-sm border-zinc-800 bg-zinc-900/70 px-4 text-sm text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-brand-pink" />
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Button onClick={save} disabled={busy} data-testid="save-notion-config"
          className="h-11 rounded-full bg-brand-pink px-7 font-semibold text-zinc-950 hover:bg-brand-pink-soft transition-colors duration-200">Save URLs</Button>
        <Button onClick={verify} disabled={busy} data-testid="verify-notion"
          className="h-11 rounded-full border border-zinc-800 bg-transparent px-7 text-zinc-300 hover:border-brand-indigo hover:text-zinc-50 transition-colors duration-200">Verify against Notion</Button>
      </div>

      <SchedulerPanel />
    </div>
  );
}

const SchedulerPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { getSchedulerConfig().then((c) => { setCfg(c); setLink(c.cal_link || ""); }); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const c = await saveSchedulerConfig({ cal_link: link, event_name: "Sabrea Introduction", duration: "30 minutes" });
      setCfg(c);
      toast.success(c.configured ? "Booking link saved." : "Booking link cleared.");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-14 border border-zinc-900 bg-zinc-900/30 p-6" data-testid="scheduler-panel">
      <p className="font-mono-tech text-[10px] text-brand-indigo">Applicant scheduler</p>
      <p className="mt-2 max-w-2xl text-sm text-zinc-400">
        Cal.com slug used by the embedded booking step at the end of the application. Format: <span className="font-mono-tech text-zinc-300">username/event-slug</span>.
      </p>
      <p className="mt-3 font-mono-tech text-[10px] text-zinc-500">
        Status: {cfg ? (cfg.configured ? `configured — ${cfg.cal_link}` : "not configured") : "loading…"} · Webhook endpoint: /api/webhooks/calcom
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <Input data-testid="cal-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="faithleadgen2/intro"
          className="h-11 min-w-[260px] flex-1 rounded-sm border-zinc-800 bg-zinc-900/70 px-4 text-sm text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-brand-pink" />
        <Button onClick={save} disabled={busy} data-testid="save-cal-link"
          className="h-11 rounded-full bg-brand-indigo px-7 font-semibold text-white hover:bg-brand-indigo-soft transition-colors duration-200">Save booking link</Button>
      </div>
    </div>
  );
};
